import type { UserProfile } from "@/services/authService";
import {
  canAccessAdminSettingsPage,
  hasTenantPermission,
  isPlatformAdminRole,
  isTenantAdminRole,
  isCustomFrontDeskRole,
  hasFrontdeskBlockPermission,
} from "@/utils/accessControl";
import { FRONTDESK_BLOCKS } from "@/constants/frontdeskPermissions";

const TENANT_ADMIN_ROUTE_PERMISSIONS: Array<{
  path: string;
  permissions: string[];
  isVisible?: (userProfile: UserProfile | null) => boolean;
}> = [
  {
    path: "/hotels",
    permissions: ["hotel_brands:read", "hotel_brands:administrate"],
  },
  { path: "/users", permissions: ["users:read", "users:administrate"] },
  {
    path: "/billing-transactions",
    permissions: ["billing:read", "billing:administrate"],
  },
  {
    path: "/settings",
    permissions: ["settings:read", "settings:administrate"],
    isVisible: canAccessAdminSettingsPage,
  },
];

function getFirstTenantAdminRoute(
  userProfile: UserProfile | null,
): string | null {
  for (const {
    path,
    permissions,
    isVisible,
  } of TENANT_ADMIN_ROUTE_PERMISSIONS) {
    if (
      (isVisible?.(userProfile) ?? true) &&
      permissions.some((p) => hasTenantPermission(userProfile, p))
    ) {
      return path;
    }
  }
  return null;
}

export function getDefaultAuthorizedRoute(
  userProfile: UserProfile | null,
): string {
  if (isPlatformAdminRole(userProfile)) {
    return "/tenants";
  }

  if (isTenantAdminRole(userProfile)) {
    return getFirstTenantAdminRoute(userProfile) ?? "/account";
  }

  if (isCustomFrontDeskRole(userProfile)) {
    const firstAllowedBlock = FRONTDESK_BLOCKS.find(({ resource }) =>
      hasFrontdeskBlockPermission(userProfile, resource),
    );
    return firstAllowedBlock?.landingPath ?? "/account";
  }

  return "/dashboard";
}
